import { useCallback, useEffect, useRef, useState } from "react";
import { Canvas, ActiveSelection, InteractiveFabricObject, util, cache } from "fabric";

// dragging a handle past the opposite side must never flip an item inside-out (negative sizes)
InteractiveFabricObject.ownDefaults.lockScalingFlip = true;
import { CUSTOM_PROPS, makeMock, curvePath } from "./objects";
import { EXPORT_SCALE } from "./presets";

const SNAP = 6;
// re-measure text once web fonts have loaded so selection boxes match the letters
const refit = (c) => { cache.clearFontCache(); c.getObjects().forEach((o) => { if (o.initDimensions && o.fontFamily) { o.initDimensions(); o.setCoords(); } }); c.requestRenderAll(); };

// Fabric canvas with zoom-to-fit, undo/redo, Publisher-style snapping guides, keyboard + clipboard.
export function useStudioCanvas({ size, onImageFiles }) {
  const elRef = useRef(null), wrapRef = useRef(null), cv = useRef(null);
  const hist = useRef({ stack: [], i: -1, lock: false });
  const clip = useRef(null), guides = useRef([]);
  // snapping choices (remembered on this computer): smart guides, grid show / snap / size
  const [snapOpts, setSnapOptsState] = useState(() => { try { return { guides: true, show: false, grid: false, size: 10, ...JSON.parse(localStorage.getItem("ds-snap") || "{}") }; } catch { return { guides: true, show: false, grid: false, size: 10 }; } });
  const snapRef = useRef(snapOpts); snapRef.current = snapOpts;
  const setSnapOpts = useCallback((p) => setSnapOptsState((o) => { const n = { ...o, ...p }; localStorage.setItem("ds-snap", JSON.stringify(n)); return n; }), []);
  const [sel, setSel] = useState(null);
  const [tick, setTick] = useState(0);
  const [zoom, setZoomState] = useState(1);
  const bump = () => setTick((t) => t + 1);

  const snapshot = useCallback(() => {
    const h = hist.current; if (h.lock || !cv.current) return;
    const json = JSON.stringify(cv.current.toObject(CUSTOM_PROPS));
    if (h.stack[h.i] === json) return;
    h.stack = h.stack.slice(0, h.i + 1).concat(json).slice(-100); h.i = h.stack.length - 1; bump();
  }, []);
  const restore = useCallback(async (json) => {
    const h = hist.current; h.lock = true;
    await cv.current.loadFromJSON(JSON.parse(json)); cv.current.requestRenderAll(); h.lock = false; setSel(null); bump();
  }, []);
  const undo = useCallback(() => { const h = hist.current; if (h.i > 0) { h.i -= 1; restore(h.stack[h.i]); } }, [restore]);
  const redo = useCallback(() => { const h = hist.current; if (h.i < h.stack.length - 1) { h.i += 1; restore(h.stack[h.i]); } }, [restore]);

  const fit = useCallback(() => {
    const c = cv.current, w = wrapRef.current; if (!c || !w) return;
    const z = Math.min(2, (w.clientWidth - 60) / size.w, (w.clientHeight - 60) / size.h);
    c.setZoom(z); c.setDimensions({ width: size.w * z, height: size.h * z }); setZoomState(z);
  }, [size.w, size.h]);
  const setZoom = useCallback((z) => { const c = cv.current; const zz = Math.max(0.1, Math.min(4, z)); c.setZoom(zz); c.setDimensions({ width: size.w * zz, height: size.h * zz }); setZoomState(zz); }, [size.w, size.h]);

  useEffect(() => {
    const c = new Canvas(elRef.current, { preserveObjectStacking: true, backgroundColor: "#ffffff", selectionColor: "rgba(33,166,236,0.12)", selectionBorderColor: "#21a6ec" });
    cv.current = c;
    // one broken item must never blank the whole page: skip it and keep drawing the rest
    c._renderObjects = (ctx, objects) => { for (const o of objects) { if (!o) continue; ctx.save(); try { o.render(ctx); } catch (e) { console.error("Design Studio: skipped drawing an item", e); } ctx.restore(); } };
    const onSel = () => { setSel(c.getActiveObject() || null); bump(); };
    c.on("selection:created", onSel); c.on("selection:updated", onSel); c.on("selection:cleared", onSel);
    ["object:added", "object:removed"].forEach((ev) => c.on(ev, snapshot));
    // never shrink an item below 8px so it can't vanish
    c.on("object:scaling", ({ target: t }) => {
      if (t.width && t.getScaledWidth() < 8) t.set({ scaleX: 8 / t.width }); if (t.height && t.getScaledHeight() < 8) t.set({ scaleY: 8 / t.height });
    });
    c.on("object:modified", ({ target: t }) => {
      // boxes are resized for real (not stretched) so rounded corners and outlines keep their shape
      if (t?.type === "rect" && (t.scaleX !== 1 || t.scaleY !== 1)) { t.set({ width: t.width * t.scaleX, height: t.height * t.scaleY, scaleX: 1, scaleY: 1 }); t.setCoords(); } guides.current = []; snapshot(); onSel(); });
    c.on("object:moving", ({ target, e }) => {
      const so = snapRef.current;
      if (e?.altKey) { guides.current = []; return; } // hold Alt to place freely
      if (so.grid) target.set({ left: Math.round(target.left / so.size) * so.size, top: Math.round(target.top / so.size) * so.size });
      if (!so.guides) { guides.current = []; return; }
      const z = c.getZoom(), W = c.width / z, H = c.height / z, t = SNAP / z;
      const w = target.getScaledWidth(), h = target.getScaledHeight();
      const xs = [0, W / 2, W], ys = [0, H / 2, H];
      c.getObjects().forEach((o) => { if (o === target || (target.contains && target.contains(o))) return; const ow = o.getScaledWidth(), oh = o.getScaledHeight(); xs.push(o.left, o.left + ow / 2, o.left + ow); ys.push(o.top, o.top + oh / 2, o.top + oh); });
      const g = [];
      for (const [off, k] of [[0, "l"], [w / 2, "c"], [w, "r"]]) { const x = xs.find((v) => Math.abs(target.left + off - v) < t); if (x !== undefined) { target.set({ left: x - off }); g.push({ x }); break; } }
      for (const [off] of [[0], [h / 2], [h]]) { const y = ys.find((v) => Math.abs(target.top + off - v) < t); if (y !== undefined) { target.set({ top: y - off }); g.push({ y }); break; } }
      guides.current = g;
    });
    c.on("text:editing:exited", ({ target }) => { if (target?.curve) { target.set({ path: curvePath(target, target.curve) }); c.requestRenderAll(); snapshot(); } });
    c.on("mouse:up", () => { if (guides.current.length) { guides.current = []; c.requestRenderAll(); } });
    c.on("after:render", ({ ctx }) => {
      if (!guides.current.length) return;
      const z = c.getZoom(); ctx.save(); ctx.strokeStyle = "#e3171b"; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
      guides.current.forEach((g) => { ctx.beginPath(); if (g.x !== undefined) { ctx.moveTo(g.x * z, 0); ctx.lineTo(g.x * z, c.height); } else { ctx.moveTo(0, g.y * z); ctx.lineTo(c.width, g.y * z); } ctx.stroke(); });
      ctx.restore();
    });
    snapshot();
    document.fonts?.ready.then(() => refit(c));
    return () => { c.dispose(); cv.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const c = cv.current; if (!c) return; fit(); c.requestRenderAll(); }, [size.w, size.h, fit]);
  useEffect(() => { const r = () => fit(); window.addEventListener("resize", r); return () => window.removeEventListener("resize", r); }, [fit]);

  // new items land in the middle of the page, stepping down/right so they don't stack exactly
  const cascade = useRef(0);
  const add = useCallback((obj, at) => {
    const c = cv.current;
    // new items always fit inside the page (small pages like Special Offer get smaller shapes)
    const scaleIn = (o) => { const k = Math.min(1, (size.w * 0.7) / o.getScaledWidth(), (size.h * 0.7) / o.getScaledHeight()); if (k < 1) o.set({ scaleX: o.scaleX * k, scaleY: o.scaleY * k }); };
    const clampIn = (o) => { const w = o.getScaledWidth(), h = o.getScaledHeight(); o.set({ left: Math.max(0, Math.min(o.left, size.w - w)), top: Math.max(0, Math.min(o.top, size.h - h)) }); o.setCoords(); };
    const k = Math.min(1, (size.w * 0.7) / obj.getScaledWidth(), (size.h * 0.7) / obj.getScaledHeight());
    if (k < 1 && obj.mock) { const d = obj.mockData; obj = makeMock(d.type, { ...d, w: d.w * k, h: d.h * k, bh: (d.bh || d.h) * k }); }
    else if (k < 1 && obj.type === "rect") obj.set({ width: obj.width * k, height: obj.height * k, rx: (obj.rx || 0) * k, ry: (obj.ry || 0) * k });
    else scaleIn(obj);
    const w = obj.getScaledWidth(), h = obj.getScaledHeight();
    if (at) obj.set({ left: at.x - w / 2, top: at.y - h / 2 });
    else { const n = cascade.current++ % 8, st = Math.min(14, size.w / 40, size.h / 40); obj.set({ left: (size.w - w) / 2 + n * st, top: (size.h - h) / 2 + n * st }); }
    clampIn(obj);
    c.add(obj); obj.setCoords(); c.setActiveObject(obj); c.requestRenderAll();
    if (obj.fontFamily) document.fonts?.load(`${obj.fontWeight || "normal"} ${obj.fontSize}px "${obj.fontFamily}"`).then(() => { refit(c); if (c.getObjects().includes(obj)) { scaleIn(obj); clampIn(obj); c.requestRenderAll(); } });
  }, [size.w, size.h]);
  const removeSel = useCallback(() => { const c = cv.current; const a = c.getActiveObjects(); if (!a.length) return; hist.current.lock = true; a.forEach((o) => c.remove(o)); hist.current.lock = false; c.discardActiveObject(); c.requestRenderAll(); snapshot(); }, [snapshot]);
  const copy = useCallback(async () => { const a = cv.current.getActiveObject(); if (a) clip.current = await a.clone(CUSTOM_PROPS); }, []);
  const paste = useCallback(async () => {
    const c = cv.current; if (!clip.current) return;
    const o = await clip.current.clone(CUSTOM_PROPS); c.discardActiveObject();
    o.set({ left: o.left + 20, top: o.top + 20, evented: true });
    if (o instanceof ActiveSelection) { o.canvas = c; o.forEachObject((x) => c.add(x)); o.setCoords(); } else c.add(o);
    clip.current.set({ left: clip.current.left + 20, top: clip.current.top + 20 }); c.setActiveObject(o); c.requestRenderAll(); snapshot();
  }, [snapshot]);
  const cut = useCallback(async () => { await copy(); removeSel(); }, [copy, removeSel]);
  const hasClip = () => !!clip.current;
  const duplicate = useCallback(async () => { await copy(); await paste(); }, [copy, paste]);
  const arrange = useCallback((how) => {
    const c = cv.current, a = c.getActiveObject(); if (!a) return;
    ({ front: () => c.bringObjectToFront(a), back: () => c.sendObjectToBack(a), forward: () => c.bringObjectForward(a), backward: () => c.sendObjectBackwards(a) })[how]();
    c.requestRenderAll(); snapshot();
  }, [snapshot]);
  const align = useCallback((how) => {
    const c = cv.current, a = c.getActiveObject(); if (!a) return;
    const z = c.getZoom(), W = c.width / z, H = c.height / z;
    const items = a instanceof ActiveSelection ? a.getObjects() : [a];
    const box = a instanceof ActiveSelection ? { l: -a.getScaledWidth() / 2, t: -a.getScaledHeight() / 2, w: a.getScaledWidth(), h: a.getScaledHeight() } : { l: 0, t: 0, w: W, h: H };
    items.forEach((o) => {
      const w = o.getScaledWidth(), h = o.getScaledHeight();
      const v = { left: box.l, center: box.l + (box.w - w) / 2, right: box.l + box.w - w, top: box.t, middle: box.t + (box.h - h) / 2, bottom: box.t + box.h - h }[how];
      o.set(["left", "center", "right"].includes(how) ? { left: v } : { top: v }); o.setCoords();
    });
    if (a instanceof ActiveSelection) a.setCoords();
    c.requestRenderAll(); snapshot(); bump();
  }, [snapshot]);
  const update = useCallback((props, obj) => { const o = obj || cv.current.getActiveObject(); if (!o) return; o.set(props); o.setCoords?.(); cv.current.requestRenderAll(); snapshot(); bump(); }, [snapshot]);
  const replace = useCallback((oldObj, newObj) => { const c = cv.current; const idx = c.getObjects().indexOf(oldObj); if (idx < 0) return; hist.current.lock = true; c.remove(oldObj); c.insertAt(idx, newObj); hist.current.lock = false; c.setActiveObject(newObj); c.requestRenderAll(); snapshot(); }, [snapshot]);

  const exportUrl = useCallback((format = "png") => {
    const c = cv.current, z = c.getZoom(); c.discardActiveObject();
    c.setZoom(1); c.setDimensions({ width: size.w, height: size.h });
    const bg = c.backgroundColor; if (format === "jpeg" && !bg) c.backgroundColor = "#ffffff";
    const url = c.toDataURL({ format, quality: 0.92, multiplier: EXPORT_SCALE });
    c.backgroundColor = bg; c.setZoom(z); c.setDimensions({ width: size.w * z, height: size.h * z }); c.requestRenderAll();
    return url;
  }, [size.w, size.h]);
  // Publisher-style "Save as Picture": only the selected items, cropped to their outline
  const exportSel = useCallback((format = "png") => {
    const c = cv.current, objs = c.getActiveObjects(); if (!objs.length) return null;
    const z = c.getZoom(); c.discardActiveObject();
    c.setZoom(1); c.setDimensions({ width: size.w, height: size.h });
    // pad by any shadow so it isn't clipped
    const rs = objs.map((o) => { o.setCoords(); const r = o.getBoundingRect(), sh = o.shadow || (o.mock && o.getObjects?.()[0]?.shadow), p = sh ? sh.blur + Math.max(Math.abs(sh.offsetX), Math.abs(sh.offsetY)) : 0; return { left: r.left - p, top: r.top - p, width: r.width + 2 * p, height: r.height + 2 * p }; });
    const l = Math.max(0, Math.min(...rs.map((r) => r.left))), t = Math.max(0, Math.min(...rs.map((r) => r.top)));
    const r = Math.min(size.w, Math.max(...rs.map((x) => x.left + x.width))), btm = Math.min(size.h, Math.max(...rs.map((x) => x.top + x.height)));
    const bg = c.backgroundColor; c.backgroundColor = format === "jpeg" ? "#ffffff" : "";
    const url = r - l < 1 || btm - t < 1 ? null : c.toDataURL({ format, quality: 0.92, multiplier: EXPORT_SCALE, left: l, top: t, width: r - l, height: btm - t, filter: (o) => objs.includes(o) });
    c.backgroundColor = bg; c.setZoom(z); c.setDimensions({ width: size.w * z, height: size.h * z });
    c.setActiveObject(objs.length > 1 ? new ActiveSelection(objs, { canvas: c }) : objs[0]); c.requestRenderAll();
    return url && { url, w: Math.round((r - l) * EXPORT_SCALE), h: Math.round((btm - t) * EXPORT_SCALE) };
  }, [size.w, size.h]);
  const thumb = useCallback(() => {
    const c = cv.current, z = c.getZoom(); const s = 240 / Math.max(size.w, size.h);
    c.setZoom(1); c.setDimensions({ width: size.w, height: size.h }); const u = c.toDataURL({ format: "png", multiplier: s });
    c.setZoom(z); c.setDimensions({ width: size.w * z, height: size.h * z }); c.requestRenderAll(); return u;
  }, [size.w, size.h]);
  const toDoc = useCallback(() => cv.current.toObject(CUSTOM_PROPS), []);
  const loadDoc = useCallback(async (doc) => {
    const c = cv.current, h = hist.current; h.lock = true;
    if (doc && doc.objects) await c.loadFromJSON(doc); else { c.clear(); c.backgroundColor = "#ffffff"; }
    c.requestRenderAll(); h.lock = false; h.stack = []; h.i = -1; snapshot(); setSel(null);
    const fams = [...new Set(c.getObjects().filter((o) => o.fontFamily).map((o) => `${o.fontWeight || "normal"} 40px "${o.fontFamily}"`))];
    Promise.all(fams.map((f) => document.fonts?.load(f))).then(() => refit(c)).catch(() => {});
  }, [snapshot]);
  const setBackground = useCallback((color) => { cv.current.backgroundColor = color; cv.current.requestRenderAll(); snapshot(); bump(); }, [snapshot]);

  // keyboard: delete, arrows, copy/paste/duplicate, undo/redo (ignored while typing in an input or editing text)
  useEffect(() => {
    const k = (e) => {
      const c = cv.current; if (!c) return;
      const a = c.getActiveObject();
      if (/INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName) || a?.isEditing) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); }
      else if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); }
      else if (mod && e.key.toLowerCase() === "c") copy();
      else if (mod && e.key.toLowerCase() === "d") { e.preventDefault(); duplicate(); }
      else if ((e.key === "Delete" || e.key === "Backspace") && a) { e.preventDefault(); removeSel(); }
      else if (a && e.key.startsWith("Arrow")) {
        e.preventDefault(); const st = e.shiftKey ? 10 : 1;
        a.set({ left: a.left + (e.key === "ArrowLeft" ? -st : e.key === "ArrowRight" ? st : 0), top: a.top + (e.key === "ArrowUp" ? -st : e.key === "ArrowDown" ? st : 0) });
        a.setCoords(); c.requestRenderAll(); snapshot(); bump();
      }
    };
    // Ctrl+V: pasted picture -> new image, otherwise paste copied objects
    const p = (e) => {
      if (/INPUT|TEXTAREA/.test(document.activeElement?.tagName) || cv.current?.getActiveObject()?.isEditing) return;
      const files = [...(e.clipboardData?.files || [])].filter((f) => f.type.startsWith("image/"));
      if (files.length) { e.preventDefault(); onImageFiles(files); } else paste();
    };
    window.addEventListener("keydown", k); window.addEventListener("paste", p);
    return () => { window.removeEventListener("keydown", k); window.removeEventListener("paste", p); };
  }, [undo, redo, copy, paste, duplicate, removeSel, snapshot, onImageFiles]);

  const h = hist.current;
  return { elRef, wrapRef, canvas: cv, sel, tick, zoom, snapOpts, setSnapOpts, fit, setZoom, add, removeSel, copy, paste, cut, hasClip, duplicate, arrange, align, update, replace,
    currentJson: () => hist.current.stack[hist.current.i] || "",
    undo, redo, canUndo: h.i > 0, canRedo: h.i < h.stack.length - 1, exportUrl, exportSel, thumb, toDoc, loadDoc, setBackground, util };
}
